import React, { useCallback, useMemo } from 'react';
import platformAdminService from '../../services/platformAdminService';
import LLMKeyMatrix from '../shared/LLMKeys/LLMKeyMatrix';
import {
  buildKeyRows,
  resolvePlatformDirect,
  resolvePartnerOwn,
  resolvePartnerChain,
} from '../shared/LLMKeys/llmKeyStatus';
import { KEYS_COPY, PLATFORM_PARTNER_COPY } from '../../constants/partnerCopy';

/**
 * One company's LLM keys through the platform `/companies/{id}/llm-keys`
 * endpoints, in one of three variants:
 *   - 'direct'      a platform-direct company: own key → platform fallback.
 *   - 'client'      a partner's client company (Partner tier guide §4.6): its own
 *                   platform-fallback flag is ignored while it has a partner, so
 *                   that column is disabled and an inheritance toggle appears.
 *                   Badges follow the partner chain, which needs the partner's
 *                   own credential rows (`partnerCredentials`).
 *   - 'partnerHome' a partner's own keys (its home company). Its fallback flag is
 *                   "Partner may fall back to platform keys" — the switch that
 *                   decides whether the whole partner tree can use platform keys.
 *
 * The parent owns the data (`credentials`) and reloads it in `onChanged`.
 */
const PlatformCompanyKeys = ({
  companyId,
  companyName,
  variant = 'direct',
  partnerName,
  providers = [],
  credentials = [],
  partnerCredentials = [],
  onChanged,
  notify,
}) => {
  const rows = useMemo(() => buildKeyRows(providers, credentials), [providers, credentials]);

  const partnerCredByProvider = useMemo(
    () => new Map((partnerCredentials || []).map((c) => [c.provider_id, c])),
    [partnerCredentials]
  );

  const resolveEffective = useCallback(
    (row) => {
      if (variant === 'client') {
        return resolvePartnerChain(row, partnerCredByProvider.get(row.providerId), {
          partnerName,
          companyName,
        });
      }
      if (variant === 'partnerHome') return resolvePartnerOwn(row);
      return resolvePlatformDirect(row);
    },
    [variant, partnerCredByProvider, partnerName, companyName]
  );

  const update = useCallback(
    async (row, payload) => {
      await platformAdminService.setCompanyLLMKey(companyId, row.providerId, payload);
      await onChanged?.();
    },
    [companyId, onChanged]
  );

  const onRemoveRow = useCallback(
    async (row) => {
      await platformAdminService.deleteCompanyLLMKey(companyId, row.providerId);
      await onChanged?.();
    },
    [companyId, onChanged]
  );

  const config = useMemo(() => {
    if (variant === 'client') {
      return {
        fallback: {
          mode: 'disabled',
          label: 'Platform fallback',
          tooltip: PLATFORM_PARTNER_COPY.companyFallbackUnderPartner,
        },
        inherit: {
          mode: 'toggle',
          label: 'Use partner key',
          tooltip: () => KEYS_COPY.inheritToggle(partnerName, companyName),
        },
        openaiBlockedText:
          "Off right now — this company can't upload files or run knowledge-base agents until an OpenAI key is available (its own or the partner's).",
        clearConfirmText: () => 'The company key is removed; its inheritance setting is kept.',
        removeConfirmText: () =>
          `Deletes this company's row for the provider. With no row, it inherits ${partnerName || 'the partner'}'s key again.`,
        savedMessage: () => KEYS_COPY.saved,
      };
    }
    if (variant === 'partnerHome') {
      return {
        keyNoun: 'partner key',
        fallback: {
          mode: 'toggle',
          label: PLATFORM_PARTNER_COPY.partnerFallbackLabel,
          tooltip: PLATFORM_PARTNER_COPY.partnerFallbackTooltip,
        },
        inherit: { mode: 'hidden' },
        openaiBlockedText:
          "Off right now — direct users and inheriting companies can't upload files or run knowledge-base agents until you add an OpenAI key or turn on fallback.",
        clearConfirmText: (row) => KEYS_COPY.clearPartnerKey(partnerName, row.name),
        removeConfirmText: () =>
          "Deletes the partner's row for this provider, including its platform-fallback setting.",
        savedMessage: () => KEYS_COPY.saved,
      };
    }
    return {
      fallback: { mode: 'toggle', label: 'Platform fallback' },
      inherit: { mode: 'hidden' },
      openaiBlockedText:
        "Off right now — with no company key and platform fallback off, this company can't upload files or run knowledge-base agents until you add an OpenAI key or turn on fallback.",
      clearConfirmText: () => 'The company key is removed but the provider row and fallback flag are kept.',
      removeConfirmText: () =>
        'Deletes the provider row for this company. The provider becomes blocked until re-added.',
    };
  }, [variant, partnerName, companyName]);

  return (
    <LLMKeyMatrix
      rows={rows}
      resolveEffective={resolveEffective}
      keyNoun={config.keyNoun || 'company key'}
      fallback={config.fallback}
      inherit={config.inherit}
      openaiBlockedText={config.openaiBlockedText}
      clearConfirmText={config.clearConfirmText}
      removeConfirmText={config.removeConfirmText}
      savedMessage={config.savedMessage}
      onSetKey={(row, payload) => update(row, payload)}
      onClearKey={(row) => update(row, { api_key: '' })}
      onRemoveRow={onRemoveRow}
      onToggleFallback={(row, next) => update(row, { allow_platform_fallback: next })}
      onToggleInherit={(row, next) => update(row, { inherit_from_partner: next })}
      notify={notify}
    />
  );
};

export default PlatformCompanyKeys;
